/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

/**
 * Internal Imports
 */
import FeatureRoute from '../featureRoute/FeatureRoute';
import CSRNList from './supportLists/CSRNList';
import CNEdList from './supportLists/CNEdList';
import SupportProfile from './SupportProfile';
import SupportIssues from './SupportIssues';
import SupportFeedback from './SupportFeedback';

import {
  clearAllDepts,
  setTaskSearchQuery,
  setUserSection,
  clearUserSection
} from '../../redux/actions';
import { useFeatures } from '../../services/Features/features';
import { useUserRolesRef } from '../../services/UserRoles/userRoles';

import { useDebounceValue } from '../../utils/debouncers';

const Support: FC<{}> = () => {
  const dispatch = useDispatch();

  const { isCSCEnabled, isCNEdEnabled } = useFeatures();
  const { isCSCRounder, isCNEdRounder } = useUserRolesRef();

  const [searchTerm, setSearchTerm] = useState<string>('');

  const dbSearchTerm = useDebounceValue(searchTerm, 250);

  const { pathname } = useLocation();

  const view = useMemo(() => pathname.split('/')[2], [pathname]);

  const subPath = useMemo(() => pathname.split('/')[3], [pathname]);

  const supportType = useMemo(
    () => (view !== 'profile' ? view : subPath),
    [view, subPath]
  );

  useEffect(() => {
    return () => {
      dispatch(clearUserSection());
      dispatch(clearAllDepts());
    };
  }, []);

  useEffect(() => {
    if (supportType) dispatch(clearAllDepts());
    dispatch(setUserSection(supportType));
  }, [supportType]);

  useEffect(() => {
    if (subPath?.includes('issues')) dispatch(setTaskSearchQuery(searchTerm));
    if (subPath?.includes('feedback')) dispatch(setTaskSearchQuery(searchTerm));
  }, [dbSearchTerm]);

  return (
    <div>
      <Routes>
        {isCSCRounder && (
          <Route path="csc" element={<Outlet />}>
            <Route index element={<Navigate to="csc/list" replace />} />
            <Route
              path="list"
              element={
                <FeatureRoute
                  component={
                    <CSRNList
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  }
                  isFeatureEnabled={isCSCEnabled}
                  offsetHeight={128}
                />
              }
            >
              <Route
                path=":csrnFilter"
                element={
                  <CSRNList
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                  />
                }
              />
            </Route>
            <Route
              path="issues"
              element={
                <FeatureRoute
                  component={
                    <SupportIssues
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  }
                  isFeatureEnabled={isCSCEnabled}
                  offsetHeight={128}
                />
              }
            />
            <Route
              path="feedback"
              element={
                <FeatureRoute
                  component={
                    <SupportFeedback
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  }
                  isFeatureEnabled={isCSCEnabled}
                  offsetHeight={128}
                />
              }
            />
          </Route>
        )}
        {isCNEdRounder && (
          <Route path="cned" element={<Outlet />}>
            <Route index element={<Navigate to="cned/list" replace />} />
            <Route
              path="list"
              element={
                <FeatureRoute
                  component={
                    <CNEdList
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  }
                  isFeatureEnabled={isCNEdEnabled}
                  offsetHeight={128}
                />
              }
            >
              <Route
                path=":cnedFilter"
                element={
                  <CNEdList
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                  />
                }
              />
            </Route>
            <Route
              path="issues"
              element={
                <FeatureRoute
                  component={
                    <SupportIssues
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  }
                  isFeatureEnabled={isCNEdEnabled}
                  offsetHeight={128}
                />
              }
            />
            <Route
              path="feedback"
              element={
                <FeatureRoute
                  component={
                    <SupportFeedback
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                    />
                  }
                  isFeatureEnabled={isCNEdEnabled}
                  offsetHeight={128}
                />
              }
            />
          </Route>
        )}
        <Route path="profile" element={<Outlet />}>
          <Route path=":supportType" element={<SupportProfile />} />
        </Route>
        <Route
          path="*"
          element={
            <Navigate
              to={localStorage.getItem('orbitLastPath') || '/'}
              replace
            />
          }
        />
      </Routes>
    </div>
  );
};

export default Support;
